import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button, Divider, Paper, TextField, Typography } from '@mui/material';

import { enqueueError } from '@paragon/notification-tools';
import { notNullish } from '@paragon/utilities';

import { createOrganization } from '../../actions/organizationActions';
import { getOrganizationsSorted } from '../../selectors/organizations';
import { RootState } from '../../store';

const validate = {
  name(value: string) {
    if (value.trim() === '') {
      return 'Please enter an Organization name';
    }

    return null;
  },

  location(value: string) {
    if (value.trim() === '') {
      return 'Please enter an Organization location';
    }

    return null;
  },
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

export interface DraftUnsavedOrganization {
  readonly name: string;
  readonly locations: readonly string[];
}

interface State {
  readonly showErrors: boolean;
  readonly newOrganization: DraftUnsavedOrganization;
}

class CreateOrganization extends React.Component<Props, State> {
  state: State = {
    showErrors: false,

    newOrganization: {
      name: '',
      locations: [''],
    },
  };

  setName(newName: string) {
    this.setState((prevState) => ({
      newOrganization: { ...prevState.newOrganization, name: newName },
    }));
  }

  setLocation(location: string) {
    this.setState((prevState) => ({
      newOrganization: { ...prevState.newOrganization, locations: [location] },
    }));
  }

  onCreate = () => {
    if (this.validate()) {
      this.props.createOrganization(this.state.newOrganization);

      this.setName('');
      this.setLocation('');
      this.setState({ showErrors: false });
    }
  };

  validate() {
    const { newOrganization } = this.state;
    const errors = [validate.name(newOrganization.name), validate.location(newOrganization.locations[0])].filter(
      notNullish,
    );

    if (errors.length > 0) {
      this.setState({ showErrors: true });
      errors.forEach((error) => {
        this.props.enqueueError(error);
      });
      return false;
    }

    return true;
  }

  render() {
    const { showErrors, newOrganization } = this.state;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '35%', p: 1 }}>
        <Typography variant="h6">Create Organization</Typography>
        <Divider sx={{ mb: 4 }} />
        <Paper sx={{ p: 2, bgcolor: 'backContrast.main' }} elevation={1}>
          <Box component="form" sx={{ mb: 2, mt: 1 }}>
            <TextField
              variant="standard"
              required
              error={showErrors && validate.name(newOrganization.name) != null}
              id="name"
              label="Name"
              sx={{ width: '100%', mb: 2 }}
              value={newOrganization.name}
              onChange={(event) => this.setName(event.target.value)}
            />
            <TextField
              variant="standard"
              required
              error={showErrors && validate.location(newOrganization.locations[0]) != null}
              id="location"
              label="Location"
              sx={{ width: '100%', mb: 2 }}
              value={newOrganization.locations[0]}
              onChange={(event) => this.setLocation(event.target.value)}
            />
          </Box>
        </Paper>
        <Button
          sx={{
            mt: 3,
            mb: 1,
            py: 1,
            width: 150,
            color: 'buttonColor.main',
            bgcolor: 'backContrast.main',
            '&:hover': {
              bgcolor: 'primary.dark',
              color: '#fff',
            },
          }}
          variant="contained"
          onClick={this.onCreate}
        >
          Create
        </Button>
      </Box>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  organizations: getOrganizationsSorted(state),
});

const actionCreators = {
  createOrganization,
  enqueueError,
};

export default connect(mapStateToProps, actionCreators)(CreateOrganization);
